import { useNavigate } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from 'react';
import { suppliersService } from '../services/suppliersService';
import { Button, Badge, Card, Input, Select, Textarea, Icon } from '../components/ui';
import { IconTruck, IconSync, IconPlus, IconChartLine, IconEdit, IconTrash, IconSearch, IconFileExport } from '@tabler/icons-react';
import { showConfirm } from '../utils/confirmUtils';
import ExportModal from '../components/ExportModal';

const emptyForm = {
  name: '',
  mf: '',
  email: '',
  phone: '',
  address: '',
  notes: '',
  isActive: true,
};

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);

  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);

  const supplierColumns = [
    { key: 'name', label: 'Fournisseur', accessor: (s) => s?.name || '' },
    { key: 'address', label: 'Adresse', accessor: (s) => s?.address || '' },
    { key: 'mf', label: 'MF', accessor: (s) => s?.mf || '' },
    { key: 'email', label: 'Email', accessor: (s) => s?.email || '' },
    { key: 'phone', label: 'Téléphone', accessor: (s) => s?.phone || '' },
    { key: 'status', label: 'Statut', accessor: (s) => (s?.isActive ?? true) ? 'Actif' : 'Inactif' },
  ];

  const closeModal = () => {
    setShowModal(false);
    setEditingSupplier(null);
    setForm(emptyForm);
    setError('');
  };

  const openCreate = () => {
    setEditingSupplier(null);
    setForm(emptyForm);
    setError('');
    setShowModal(true);
  };

  const openEdit = (supplier) => {
    setEditingSupplier(supplier);
    setForm({
      name: supplier?.name || '',
      mf: supplier?.mf || '',
      email: supplier?.email || '',
      phone: supplier?.phone || '',
      address: supplier?.address || '',
      notes: supplier?.notes || '',
      isActive: supplier?.isActive ?? true,
    });
    setError('');
    setShowModal(true);
  };

  const normalizeListResponse = (data) => {
    // On supporte 2 formats: [] ou { items: [] }
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.items)) return data.items;
    return [];
  };

  const loadSuppliers = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await suppliersService.list();
      setSuppliers(normalizeListResponse(data));
    } catch (e) {
      // Backend pas prêt => on affiche proprement, sans casser la page
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        'Backend non disponible pour le module Achats (Fournisseurs).';
      setError(msg);
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSuppliers();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validate = () => {
    if (!form.name.trim()) return 'Le nom du fournisseur est obligatoire.';
    // MF optionnel v1, donc pas de validation stricte ici
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) {
      setError(v);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        name: form.name.trim(),
        mf: form.mf.trim() || undefined,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        address: form.address.trim() || undefined,
        notes: form.notes.trim() || undefined,
        isActive: !!form.isActive,
      };

      if (editingSupplier?._id) {
        await suppliersService.update(editingSupplier._id, payload);
      } else if (editingSupplier?.id) {
        // fallback si ton backend utilise id au lieu de _id
        await suppliersService.update(editingSupplier.id, payload);
      } else {
        await suppliersService.create(payload);
      }

      closeModal();
      await loadSuppliers();
    } catch (e2) {
      const msg = e2?.response?.data?.message || e2?.message || 'Erreur lors de l’enregistrement.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (supplier) => {
    const id = supplier?._id || supplier?.id;
    if (!id) {
      setError("Impossible de supprimer: identifiant manquant.");
      return;
    }

    if (!await showConfirm('Êtes-vous sûr de vouloir supprimer ce fournisseur ?')) return;
    setLoading(true);
    setError('');
    try {
      await suppliersService.remove(id);
      await loadSuppliers();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Erreur lors de la suppression.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const filteredSuppliers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return suppliers;

    return suppliers.filter((s) => {
      const name = (s?.name || '').toLowerCase();
      const mf = (s?.mf || '').toLowerCase();
      const email = (s?.email || '').toLowerCase();
      const phone = (s?.phone || '').toLowerCase();
      return name.includes(q) || mf.includes(q) || email.includes(q) || phone.includes(q);
    });
  }, [suppliers, search]);

  return (
    <div className="flex-1 p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Fournisseurs</h1>
        <p className="text-gray-600">Gérez vos fournisseurs (module Achats).</p>
      </div>

      {/* Actions */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Liste des fournisseurs</h3>
          <p className="text-sm text-gray-500">{filteredSuppliers.length} fournisseur(s)</p>
        </div>

        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => setShowExportModal(true)} disabled={filteredSuppliers.length === 0}>
            <IconFileExport size={15} strokeWidth={1.6} className="mr-1" />
            Export
          </Button>
          <Button variant="secondary" size="sm" onClick={loadSuppliers} disabled={loading}>
            <Icon name="IconSync" size={16} className="mr-1" />
            Rafraîchir
          </Button>

          <Button variant="primary" size="sm" onClick={openCreate}>
            <Icon name="IconPlus" size={16} className="mr-1" />
            Nouveau fournisseur
          </Button>
        </div>
      </div>

      {/* Erreur globale */}
      {!!error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-sm text-red-800 font-semibold">Erreur</div>
          <div className="text-sm text-red-700 mt-1">{error}</div>
          <div className="text-xs text-red-600 mt-2">
            Si tu n’as pas encore créé le backend Achats, c’est normal: l’API /suppliers n’existe pas encore.
          </div>
        </div>
      )}

      {/* Recherche */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden">
        <div className="p-5 border-b border-gray-200">
          <h4 className="font-semibold text-gray-800">Recherche</h4>
        </div>
        <div className="p-5">
          <label className="block text-sm font-medium mb-2 text-gray-700">Nom / MF / Email / Téléphone</label>
          <Input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Ex: Fournisseur ABC, 1234567ABC000, contact@..."
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto" />
            <p className="mt-2 text-gray-600">Chargement...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Fournisseur</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">MF</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Contact</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Statut</th>
                  <th className="p-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredSuppliers.map((s) => {
                  const id = s?._id || s?.id;
                  return (
                    <tr key={id || s?.mf || s?.name} className="hover:bg-gray-50 transition-colors">
                      <td className="p-3">
                        <div className="font-medium text-gray-900 text-sm truncate max-w-[260px]" title={s?.name || ''}>
                          {s?.name || 'Non renseigné'}
                        </div>
                        <div className="text-xs text-gray-500 truncate max-w-[260px]" title={s?.address || ''}>
                          {s?.address || '—'}
                        </div>
                      </td>

                      <td className="p-3">
                        <div className="font-mono text-xs text-gray-700 truncate max-w-[140px]" title={s?.mf || ''}>
                          {s?.mf || '—'}
                        </div>
                      </td>

                      <td className="p-3">
                        <div className="text-sm text-gray-900 truncate max-w-[220px]" title={s?.email || ''}>
                          {s?.email || '—'}
                        </div>
                        <div className="text-xs text-gray-500 truncate max-w-[220px]" title={s?.phone || ''}>
                          {s?.phone || '—'}
                        </div>
                      </td>

                      <td className="p-3">
                      <Badge variant={(s?.isActive ?? true) ? 'success' : 'danger'}>
                        {(s?.isActive ?? true) ? 'Actif' : 'Inactif'}
                      </Badge>
                      </td>

                      <td className="p-3">
                        <div className="flex gap-1">
                          <button
                            onClick={() => navigate(`/suppliers/${s._id || s.id}/statement`)}
                            className="p-1.5 rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors"
                            title="État détaillé"
                            type="button"
                          >
                            <Icon name="IconChartLine" size={16} />
                          </button>
                          <button
                            onClick={() => openEdit(s)}
                            className="p-1.5 rounded-lg border border-amber-200 text-amber-600 hover:bg-amber-50 transition-colors"
                            title="Modifier"
                            type="button"
                          >
                            <Icon name="IconEdit" size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(s)}
                            className="p-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                            title="Supprimer"
                            type="button"
                          >
                            <Icon name="IconTrash" size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {!loading && filteredSuppliers.length === 0 && (
              <div className="p-12 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                  <Icon name="IconTruck" size={32} className="text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">Aucun fournisseur</h3>
                <p className="text-gray-500 mb-6">
                  Commencez par créer votre premier fournisseur.
                </p>
                <Button variant="primary" onClick={openCreate}>
                  <Icon name="IconPlus" size={16} className="mr-1" />
                  Créer un fournisseur
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal create/edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl flex flex-col" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
              <div>
                <h2 className="text-lg font-bold text-gray-800">
                  {editingSupplier ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">Remplissez les informations ci-dessous.</p>
              </div>
              <button
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={closeModal}
                aria-label="Fermer"
                type="button"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {!!error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="text-sm text-red-800 font-semibold">Erreur</div>
                  <div className="text-sm text-red-700 mt-1">{error}</div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    Nom fournisseur <span className="text-red-600">*</span>
                  </label>
                  <Input name="name" value={form.name} onChange={handleChange} placeholder="Ex: Société ABC" />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">Matricule fiscal (MF)</label>
                  <Input name="mf" value={form.mf} onChange={handleChange} placeholder="Ex: 1234567ABC000" className="font-mono" />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">Email</label>
                  <Input name="email" value={form.email} onChange={handleChange} placeholder="contact@fournisseur.tn" />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">Téléphone</label>
                  <Input name="phone" value={form.phone} onChange={handleChange} placeholder="Ex: 71234567" />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">Adresse</label>
                  <Input name="address" value={form.address} onChange={handleChange} placeholder="Rue, ville, code postal..." />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2 text-gray-700">Notes</label>
                  <Textarea name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="Remarques internes..." />
                </div>

                <div className="md:col-span-2 flex items-center gap-2">
                  <input
                    id="isActive"
                    name="isActive"
                    type="checkbox"
                    checked={!!form.isActive}
                    onChange={handleChange}
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-700">
                    Fournisseur actif
                  </label>
                </div>

                <div className="md:col-span-2 flex justify-end gap-2 pt-2 border-t">
                  <Button variant="secondary" onClick={closeModal} disabled={loading}>
                    Annuler
                  </Button>
                  <Button variant="primary" type="submit" disabled={loading}>
                    {loading ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showExportModal && (
        <ExportModal
          columns={supplierColumns}
          data={filteredSuppliers}
          title="Liste des fournisseurs"
          filename="fournisseurs"
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
}
